/**
 * Which clip plays for an ability (pure). Lookup order for a hero of class `classKey`:
 *   1. the ability's own clip for that phase (`registerAbilityClips`);
 *   2. the class default (`registerClassDefaults`): swing / spell for strikes (by the cue kind:
 *      melee swing vs spell gesture), cast, channel;
 *   3. the generic default.
 *
 * Phases:
 *   strike   one-shot. Plays on the predicted key press for instants, and on the sim's cast
 *            success (cast-time spells release, melee, auto-attacks).
 *   cast     loops while a cast bar runs (predicted bar included).
 *   channel  loops while channelling.
 */
import { BASE_VISUAL_ALIAS } from '@keystone/content'; // +classes
import type { Clip, ClipPhase } from './clips';

export interface AbilityClips {
  strike?: Clip;
  cast?: Clip;
  channel?: Clip;
}

export interface ClassClips {
  /** Melee strikes and auto-attacks. */
  swing?: Clip;
  /** Instant spells and the release of cast-time spells. */
  spell?: Clip;
  cast?: Clip;
  channel?: Clip;
}

const byAbility = new Map<string, AbilityClips>();
const byClass = new Map<string, ClassClips>();
let generic: Required<ClassClips> | null = null;

/** Register (or extend) the clips of one ability id, e.g. `ABILITY.blademaster.mortalStrike`. */
export function registerAbilityClips(abilityId: string, clips: AbilityClips): void {
  byAbility.set(abilityId, { ...byAbility.get(abilityId), ...clips });
}

/** Per-class fallbacks for abilities without their own clip (class trail colours, dual wield). */
export function registerClassDefaults(classKey: string, clips: ClassClips): void {
  byClass.set(classKey, { ...byClass.get(classKey), ...clips });
}

export function registerGenericDefaults(clips: Required<ClassClips>): void {
  generic = clips;
}

export function abilityClips(abilityId: string): AbilityClips | undefined {
  return byAbility.get(abilityId) ?? byAbility.get(BASE_VISUAL_ALIAS[abilityId] ?? ''); // +classes
}

/** Every registered ability id (debug browser). */
export function registeredAbilities(): string[] {
  return [...byAbility.keys()];
}

/** The clip to play; null only before the defaults are registered. */
export function resolveClip(abilityId: string | null | undefined, phase: ClipPhase, classKey: string, cueKind: 'swing' | 'spell' = 'spell'): Clip | null {
  const own = abilityId ? (byAbility.get(abilityId)?.[phase] ?? byAbility.get(BASE_VISUAL_ALIAS[abilityId] ?? '')?.[phase]) : undefined; // +classes: base kits reuse the old clips
  if (own) return own;
  const key = phase === 'strike' ? cueKind : phase;
  return byClass.get(classKey)?.[key] ?? generic?.[key] ?? null;
}

/** Test helper: forget everything registered. */
export function clearClipRegistry(): void {
  byAbility.clear();
  byClass.clear();
  generic = null;
}
